import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProjectSharesRequest,
  updateSharePermissionRequest,
  revokeShareRequest,
} from "../../redux/projects/projectsSlice";
import default_theme from "../../assets/themes/themes";

const ProjectMembersList = ({ projectId, isOwner, ownerId, currentUserId }) => {
  const dispatch = useDispatch();
  const { shares, sharesLoading, sharesError } = useSelector(
    (state) => state.projects
  );
  
  // Track if we've successfully loaded shares at least once
  const [hasLoadedShares, setHasLoadedShares] = useState(false);

  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [shareToRevoke, setShareToRevoke] = useState(null);
  const [memberToRevoke, setMemberToRevoke] = useState(null);

  useEffect(() => {
    if (projectId && isOwner) {
      dispatch(fetchProjectSharesRequest({ projectId }));
    }
  }, [projectId, isOwner, dispatch]);

  // Track when shares are successfully loaded
  useEffect(() => {
    const sharesArray = Array.isArray(shares) ? shares : (shares?.data || []);
    if (!sharesLoading && sharesArray.length >= 0) {
      setHasLoadedShares(true);
    }
  }, [shares, sharesLoading]);

  const handlePermissionChange = (shareId, newPermission) => {
    dispatch(
      updateSharePermissionRequest({
        projectId,
        shareId,
        permission: newPermission,
      })
    );
  };

  const handleRevokeClick = (shareId, memberEmail) => {
    setShareToRevoke(shareId);
    setMemberToRevoke(memberEmail);
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = () => {
    if (shareToRevoke) {
      dispatch(revokeShareRequest({ projectId, shareId: shareToRevoke }));
      setRevokeDialogOpen(false);
      setShareToRevoke(null);
      setMemberToRevoke(null);
    }
  };

  const handleRevokeCancel = () => {
    setRevokeDialogOpen(false);
    setShareToRevoke(null);
    setMemberToRevoke(null);
  };

  if (!isOwner) {
    return null; // Only owners can see the members list
  }

  const sharesArray = Array.isArray(shares) ? shares : (shares?.data || []);
  
  // Determine if error is from sharing (should be ignored here) vs fetching (should be shown)
  // Sharing errors typically contain phrases like "already has access", "already been sent", "No user found"
  // Fetch errors are more generic like "Failed to load" or network errors
  const isSharingError = sharesError && (
    sharesError.includes("already has access") ||
    sharesError.includes("already been sent") ||
    sharesError.includes("No user found") ||
    sharesError.includes("email address")
  );
  
  if (sharesLoading && sharesArray.length === 0 && !hasLoadedShares) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Only show error if it's a fetch error (not a sharing error)
  // If we have shares data or have loaded before, always show the list even if there's a sharing error
  if (sharesError && !isSharingError && !hasLoadedShares && sharesArray.length === 0) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {sharesError}
      </Alert>
    );
  }

  // Separate accepted and pending shares
  // Filter out pending shares if the user already has an accepted share (to prevent duplicates)
  const acceptedUserIds = new Set(
    sharesArray
      .filter((share) => share.status === "accepted" && share.user_id)
      .map((share) => share.user_id)
  );
  
  const acceptedShares = sharesArray.filter((share) => share.status === "accepted");
  const pendingShares = sharesArray.filter(
    (share) => 
      share.status === "pending" && 
      (!share.user_id || !acceptedUserIds.has(share.user_id))
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, color: default_theme.palette.other.figmaBlue }}>
        Project Members
      </Typography>

      {/* Owner */}
      <List>
        <ListItem>
          <ListItemText
            primary={ownerId === currentUserId ? "You (Owner)" : "Owner"}
            secondary={ownerId}
          />
          <Chip label="Owner" color="secondary" size="small" />
        </ListItem>
      </List>

      {acceptedShares.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
            Collaborators
          </Typography>
          <List>
            {acceptedShares.map((share) => (
              <ListItem key={share.id}>
                <ListItemText
                  primary={share.email || `User ${share.user_id}`}
                  secondary={share.invited_by_name ? `Invited by ${share.invited_by_name}` : ""}
                />
                <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
                  <Select
                    value={share.permission}
                    onChange={(e) => handlePermissionChange(share.id, e.target.value)}
                    disabled={sharesLoading}
                  >
                    <MenuItem value="viewer">Viewer</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => handleRevokeClick(share.id, share.email || `User ${share.user_id}`)}
                  disabled={sharesLoading}
                  size="small"
                  sx={{ color: default_theme.palette.other.red }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {pendingShares.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
            Pending Invitations
          </Typography>
          <List>
            {pendingShares.map((share) => (
              <ListItem key={share.id}>
                <ListItemText
                  primary={share.email}
                  secondary={`Invited by ${share.invited_by_name || "Unknown"}`}
                />
                <Chip
                  label={share.permission === "viewer" ? "Viewer" : "Editor"}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <IconButton
                  onClick={() => handleRevokeClick(share.id, share.email)}
                  disabled={sharesLoading}
                  size="small"
                  sx={{ color: default_theme.palette.other.red }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {acceptedShares.length === 0 && pendingShares.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No collaborators yet. Share the project to invite others.
        </Typography>
      )}

      <Dialog
        open={revokeDialogOpen}
        onClose={handleRevokeCancel}
        aria-labelledby="revoke-dialog-title"
        aria-describedby="revoke-dialog-description"
      >
        <DialogTitle id="revoke-dialog-title">
          Remove Member
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="revoke-dialog-description">
            Are you sure you want to remove <strong>{memberToRevoke}</strong> from this project? 
            They will no longer have access to this project and it will disappear from their projects list.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevokeCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleRevokeConfirm}
            color="error"
            variant="contained"
            autoFocus
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectMembersList;
